import { useCallback, useEffect } from 'react';

import { useGetAlbum } from '@vimeo/gctl-api/hooks/useAlbum';
import { LayoutConfig } from '@vimeo/gctl-api/models/components/schemas/layout-config/LayoutConfig';
import { PlaybackConfig } from '@vimeo/gctl-api/models/components/schemas/playback-config/PlaybackConfig';
import { useViewer } from '@vimeo/viewer';
import { useIsClipView } from '@vimeo/viewer-showcase-ui/hooks/use-is-clip-view';
import { getConfigProperty } from '@vimeo/viewer-showcase-ui/utils/helper';
import { GridSize } from '@vimeo/viewer-showcase-ui/utils/types';

import { useShareLoopParams } from './use-share-loop-params';
import { useShowcaseSSR } from '../context/ShowcaseSSRContext';
import { useAlbumStore } from '../state/albumStore';
import { ALBUM_SORT_MAP, DEFAULT_SORT, SHOWCASE_ALBUM_FIELDS } from '../utils/constants';

const layoutMap: { [key: string]: string } = {
  grid: 'default',
  player: 'featured',
  live: 'live',
  playlist: 'playlist', // do not show header for this layout
};

export const useGetShowcase = () => {
  const {
    albumId,
    password,
    isShowcaseEmbedded,
    isOffsite,
    referrer,
    setNoOfVideos,
    setSeoAllowIndex,
    setAlbumPrivacy,
    setAlbumOwnerId,
  } = useAlbumStore((state) => ({
    albumId: state.albumId,
    password: state.hashedPass,
    isShowcaseEmbedded: state.isShowcaseEmbedded, // Comes from EmbedShowcaseContext https://github.com/vimeows/vimeo/blob/master/frontend/apps/embed/libs/showcase-layout/ShowcasePage.tsx#L34
    isOffsite: state.isOffsite,
    referrer: state.referrer,
    setNoOfVideos: state.setNoOfVideos,
    setSeoAllowIndex: state.setSeoAllowIndex,
    setAlbumPrivacy: state.setAlbumPrivacy,
    setAlbumOwnerId: state.setAlbumOwnerId,
  }));

  const ssrData = useShowcaseSSR();
  const isClipView = useIsClipView();
  const viewer = useViewer();
  const { data, isLoading } = useGetAlbum(
    () => {
      if (!viewer) return null;
      return {
        where: { albumId },
        select: [...SHOWCASE_ALBUM_FIELDS],
        query: {
          isEmbed: isShowcaseEmbedded,
          // The album API only uses the `referrer` query param when is_embed=true
          // (for offsite access control). When is_embed=false it reads the HTTP
          // Referer header instead. See AlbumRequestHelper.php and Request.php::getAlbum().
          ...(isShowcaseEmbedded && { referrer }),
          ...(password && { password }),
        },
      };
    },
    ssrData?.albumData ? { fallbackData: ssrData.albumData } : undefined,
  );

  /**
   * Do not show vimeo navigation for any of the following cases -
   * 1. Viewing showcase on offsite
   * 2. Viewing showcase on embed page
   * 3. User has disabled vimeo navigation for this showcase
   * 4. Showcase layout type is playlist
   */
  // Layout schema only returns > layout: 'grid' | 'player'
  const isPlaylistLayout = data?.layout && layoutMap[data.layout] === 'playlist';

  const shouldHideNavigation = data?.hideNav || isOffsite || isShowcaseEmbedded || isPlaylistLayout;

  const sortConfig = data?.sort ? ALBUM_SORT_MAP[data.sort] : DEFAULT_SORT;

  const totalVideos = data?.metadata?.connections?.videos?.total || null;
  const seoAllowIndex = typeof data?.seoAllowIndexed === 'boolean' ? data?.seoAllowIndexed : null;
  const privacyView = data?.privacy?.view || null;
  const albumOwnerId = data?.user?.uri?.split('/').pop();
  const { hasShareLoopTrackingParams } = useShareLoopParams();
  const shareLoopParams = hasShareLoopTrackingParams ? '?share=copy&fl=sc&fe=fs' : '?share=copy';

  const {
    banner,
    layout: layoutConfig,
    playback: playbackConfig,
    showVideoDetails,
    showVideoPlayerCards,
  } = data?.config || {};

  // Helper for playback config extraction
  const getPlaybackConfig = useCallback(
    (key: keyof PlaybackConfig) => Boolean(getConfigProperty(playbackConfig?.[key])),
    [playbackConfig],
  );

  // Helper for playback and layout config extraction for video grid
  const getGridConfig = useCallback(
    (playbackKey: keyof PlaybackConfig, layoutKey: keyof LayoutConfig) => {
      const config = isClipView ? playbackConfig?.[playbackKey] : layoutConfig?.[layoutKey];
      return Boolean(getConfigProperty(config));
    },
    [isClipView, layoutConfig, playbackConfig],
  );

  useEffect(() => {
    typeof totalVideos === 'number' && setNoOfVideos(totalVideos);
  }, [setNoOfVideos, totalVideos]);

  useEffect(() => {
    typeof seoAllowIndex === 'boolean' && setSeoAllowIndex(seoAllowIndex);
  }, [seoAllowIndex, setSeoAllowIndex]);

  useEffect(() => {
    privacyView && setAlbumPrivacy(privacyView);
  }, [privacyView, setAlbumPrivacy]);

  useEffect(() => {
    albumOwnerId && setAlbumOwnerId(Number(albumOwnerId) || 0);
  }, [albumOwnerId, setAlbumOwnerId]);

  const showPlayerCards = Boolean(getConfigProperty(showVideoPlayerCards)) || false;
  return {
    allowContinuousPlay: true,
    user: data?.user,
    layout: layoutMap[data?.layout || 'grid'],
    albumLayout: data?.layout,
    title: data?.name || '',
    description: data?.description || '',
    allowShare: data?.allowShare || false,
    customLogo: data?.webCustomLogo
      ? data?.customLogo?.sizes?.[data?.customLogo?.sizes?.length - 1].link
      : '',
    thumbnailUrl: data?.hasFeaturedContent // This will cause an issue with the legacy showcases transition as hasFeaturedContent is defaulted to false
      ? data?.pictures?.sizes?.[data?.pictures?.sizes?.length - 1].link
      : '',
    hideNav: shouldHideNavigation,
    colorMode: data?.theme === 'dark' ? 'dark' : 'light',
    isLoading: isLoading || !data, // show loading before data is fetched
    ...(data?.brandColor &&
      data?.webBrandColor && {
        brandColor: `#${data?.brandColor}`,
        playerAccentColor: data?.brandColor,
      }),
    theme: data?.theme,
    loop: data?.loop,
    // Do not use this `isEmpty` as the video total in metadata is not reliable. Use `total` from GET album videos call instead.
    isEmpty: data?.metadata?.connections?.videos?.total === 0,
    sortConfig,
    canEditShowcase: !!data?.embed && !isOffsite && !isShowcaseEmbedded,
    allowDownloads: data?.allowDownloads,
    hideUpcoming: data?.hideUpcoming,
    hasFeaturedContent: data?.hasFeaturedContent,
    shareLink:
      isOffsite && data?.domain
        ? `${data.domain}${shareLoopParams}`
        : `${viewer.vimeoHttpsUrl}/showcase/${data?.url ?? albumId}${shareLoopParams}`,
    gridSize: (isClipView
      ? getConfigProperty(playbackConfig?.playbackGridSize)
      : getConfigProperty(layoutConfig?.gridSize)) as GridSize,
    playback: {
      enableAskAi: getPlaybackConfig('enableAskAi'),
      enableComments: getPlaybackConfig('enableComments'),
      enableLike: getPlaybackConfig('enableLike'),
      enableShare: getPlaybackConfig('enableShare'),
      showDateAdded: getPlaybackConfig('showPlaybackAddedDate'),
      showDescription: getPlaybackConfig('showPlaybackVideoDescription'),
      showProfileImage: getPlaybackConfig('showPlaybackProfileImage'),
      showProfileName: getPlaybackConfig('showPlaybackProfileName'),
      showTitle: getPlaybackConfig('showPlaybackVideoTitle'),
      showViews: getPlaybackConfig('showPlaybackViews'),
    },
    showGridProfileImage: getGridConfig('showPlaybackGridProfileImage', 'showGridProfileImage'),
    showGridProfileName: getGridConfig('showPlaybackGridProfileName', 'showGridProfileName'),
    showGridVideoTitle: getGridConfig('showPlaybackGridVideoTitle', 'showGridVideoTitles'),
    showVideoCount: getGridConfig('showPlaybackVideoCount', 'showGridVideoCount'),
    showGridVideoCard: isClipView
      ? Boolean(getConfigProperty(playbackConfig?.showPlaybackGridVideoCard))
      : showPlayerCards,
    showVideoDetails: Boolean(getConfigProperty(showVideoDetails)),
    showBannerDescription: Boolean(getConfigProperty(banner?.showBannerDescription)),
    showBannerTitle: Boolean(getConfigProperty(banner?.showBannerTitle)),
    showSearchBar: Boolean(getConfigProperty(data?.config?.navigation?.showSearchBar)),
    showBannerHeader: Boolean(getConfigProperty(banner?.showBannerHeader)),
    showStartWatchingButton: Boolean(getConfigProperty(banner?.showStartWatchingButton)),
    textCtaAlignment: String(getConfigProperty(data?.config?.textCtaAlignment)),
  };
};
